/**
 * Trial scorecard — sense hub + multi-metric grade.
 *
 * One MCP tool shape is polled once (sense hub). Many metrics and an optional
 * drive signal subscribe to that snapshot. Benchmarks grade a scorecard, not
 * a single counter.
 */
import { z } from 'zod';
import { MetricAggregateSchema, MetricBaselineModeSchema, MetricComparatorSchema, } from './metric-value.js';
import { parseWatchSelector } from './watch-selector.js';
/** Fact lane holding the active trial scorecard board. */
export const trialScorecardLaneId = 'trial_scorecard';
/** Snapshot fact lane for a sense hub: `sense:<senseId>`. */
export function senseHubLaneId(senseId) {
    return `sense:${senseId}`;
}
const SelectorFieldSchema = z
    .string()
    .min(1)
    .max(500)
    .refine((s) => parseWatchSelector(s) !== null, { message: 'Invalid selector' });
/** One poll plan — unique per (connection, tool, args) in a trial. */
export const TrialSenseHubSchema = z.object({
    /** Stable id (e.g. ops_summary). */
    id: z
        .string()
        .min(1)
        .max(64)
        .regex(/^[a-z][a-z0-9_]*$/, 'sense id: snake_case starting with a letter'),
    mcpConnectionId: z.string().min(1),
    toolName: z.string().min(1).max(200),
    args: z.record(z.string(), z.unknown()).default({}),
    intervalSec: z.number().int().min(2).max(3_600).default(10),
});
/** One grade line on the scorecard. */
export const TrialScoreMetricSchema = z.object({
    id: z
        .string()
        .min(1)
        .max(64)
        .regex(/^[a-z][a-z0-9_]*$/, 'metric id: snake_case'),
    /** Operator label (defaults to id). */
    label: z.string().min(1).max(200).optional(),
    /** Which sense hub snapshot to read. */
    senseId: z.string().min(1).max(64),
    /** Field on the tool JSON (e.g. events_resolved). */
    selector: SelectorFieldSchema,
    aggregate: MetricAggregateSchema.default('value'),
    comparator: MetricComparatorSchema,
    /**
     * Pass line. With baselineMode=delta this is change-since-start
     * (e.g. +10 resolves), not an absolute counter.
     */
    target: z.number().finite(),
    baselineMode: MetricBaselineModeSchema.default('delta'),
    /**
     * Outcomes make progress toward completion, guardrails describe live health,
     * and diagnostics are displayed without grading the run.
     */
    kind: z.enum(['outcome', 'guardrail', 'diagnostic']).optional(),
});
/** When to wake the agent (drive) — never used for grading. */
export const TrialDriveSignalSchema = z.object({
    senseId: z.string().min(1).max(64),
    selector: SelectorFieldSchema,
    op: z.enum(['gt', 'gte', 'lt', 'lte', 'eq', 'neq']).default('gt'),
    value: z.union([z.number(), z.boolean(), z.string()]).default(0),
    wakeDelivery: z.enum(['next-turn', 'when-idle']).default('when-idle'),
    /** Min seconds between drive wakes (coalesce). */
    coalesceSec: z.number().int().min(5).max(3_600).default(60),
    wakePrompt: z.string().min(1).max(2_000).optional(),
});
export const TrialScorecardStatusSchema = z.enum(['active', 'completed', 'cancelled']);
export const TrialMetricLineStatusSchema = z.enum([
    // Legacy persisted values remain readable and are normalized on the next tick.
    'pending',
    'pass',
    'fail',
    'warming',
    'in_progress',
    'achieved',
    'healthy',
    'violated',
    'observed',
    'unavailable',
    'failed',
]);
/** Live per-metric progress on the board. */
export const TrialMetricProgressSchema = TrialScoreMetricSchema.extend({
    baselineValue: z.number().nullable().optional(),
    lastValue: z.number().nullable().optional(),
    lastEvaluatedAt: z.string().datetime().nullable().optional(),
    lineStatus: TrialMetricLineStatusSchema.default('warming'),
});
/** Live sense hub schedule on the board. */
export const TrialSenseHubStateSchema = TrialSenseHubSchema.extend({
    nextPollAt: z.string().datetime().nullable().optional(),
    lastPolledAt: z.string().datetime().nullable().optional(),
    lastError: z.string().max(500).nullable().optional(),
});
export const TrialDriveStateSchema = TrialDriveSignalSchema.extend({
    lastWakeAt: z.string().datetime().nullable().optional(),
});
/**
 * PUT body / kit scorecard — declarative only (no live clocks).
 */
function createTrialScorecardInputSchema(senseSchema) {
    return z
        .object({
        /** Optional agent-facing objective blurb. */
        objective: z.string().min(1).max(2_000).optional(),
        senses: z.array(senseSchema).min(1).max(10),
        metrics: z.array(TrialScoreMetricSchema).min(1).max(30),
        drive: TrialDriveSignalSchema.nullable().optional(),
        /** Keep polling and driving after metrics pass; intended for open-ended operations. */
        continuous: z.boolean().optional(),
        deadlineSeconds: z
            .number()
            .int()
            .min(60)
            .max(30 * 24 * 3_600)
            .optional(),
    })
        .superRefine((value, ctx) => {
        const v = value;
        const senseIds = new Set(v.senses.map((s) => s.id));
        if (senseIds.size !== v.senses.length) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Duplicate sense id',
                path: ['senses'],
            });
        }
        const metricIds = new Set(v.metrics.map((m) => m.id));
        if (metricIds.size !== v.metrics.length) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Duplicate metric id',
                path: ['metrics'],
            });
        }
        for (const m of v.metrics) {
            if (!senseIds.has(m.senseId)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Metric ${m.id} references unknown sense ${m.senseId}`,
                    path: ['metrics'],
                });
            }
        }
        if (v.drive && !senseIds.has(v.drive.senseId)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Drive references unknown sense ${v.drive.senseId}`,
                path: ['drive'],
            });
        }
        // Same tool+args should be one hub (warn via refine as error — force merge).
        const keys = new Map();
        for (const s of v.senses) {
            const k = `${s.mcpConnectionId ?? 'auto'}|${s.toolName}|${stableArgsKey(s.args)}`;
            const prev = keys.get(k);
            if (prev) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Senses "${prev}" and "${s.id}" poll the same tool — merge into one hub`,
                    path: ['senses'],
                });
            }
            keys.set(k, s.id);
        }
    });
}
export const PutTrialScorecardInputSchema = createTrialScorecardInputSchema(TrialSenseHubSchema);
/**
 * Agent-facing scorecard input. The host resolves the exact connection from
 * the live MCP tool exposed to the agent, so the model never needs to discover
 * or guess an internal connection id.
 */
export const ConfigureScorecardSenseHubInputSchema = TrialSenseHubSchema.extend({
    mcpConnectionId: z.string().min(1).optional(),
});
export const ConfigureScorecardInputSchema = createTrialScorecardInputSchema(ConfigureScorecardSenseHubInputSchema);
/** @deprecated Use the operation-independent scorecard names above. */
export const ConfigureOperationSenseHubInputSchema = ConfigureScorecardSenseHubInputSchema;
/** @deprecated Use the operation-independent scorecard names above. */
export const ConfigureOperationScorecardInputSchema = ConfigureScorecardInputSchema;
/** Board payload on `trial_scorecard` fact lane. */
export const TrialScorecardFactSchema = z.object({
    runId: z.string().min(1),
    objective: z.string().max(2_000).nullable(),
    status: TrialScorecardStatusSchema,
    startedAt: z.string().datetime(),
    deadlineAt: z.string().datetime().nullable(),
    completedAt: z.string().datetime().nullable().optional(),
    continuous: z.boolean().default(false),
    senses: z.array(TrialSenseHubStateSchema).min(1),
    metrics: z.array(TrialMetricProgressSchema).min(1),
    drive: TrialDriveStateSchema.nullable().optional(),
});
export function parseTrialScorecardFact(value) {
    const parsed = TrialScorecardFactSchema.safeParse(value);
    return parsed.success ? parsed.data : null;
}
export function stableArgsKey(args) {
    try {
        return JSON.stringify(args ?? {}, Object.keys(args ?? {}).sort());
    }
    catch {
        return '{}';
    }
}
/** Effective pass target (absolute or baseline+delta). */
export function effectiveScoreTarget(m) {
    const mode = m.baselineMode ?? 'delta';
    if (mode === 'delta') {
        if (m.baselineValue == null || !Number.isFinite(m.baselineValue))
            return null;
        return m.baselineValue + m.target;
    }
    return m.target;
}
export function formatScoreMetricLine(m) {
    const label = m.label ?? m.id;
    const eff = effectiveScoreTarget(m);
    const last = m.lastValue == null ? 'n/a' : String(m.lastValue);
    const mode = m.baselineMode ?? 'delta';
    const need = mode === 'delta'
        ? eff == null
            ? `Δ${m.comparator} ${m.target} (base pending)`
            : `Δ${m.comparator} ${m.target} (need ${eff}, base ${m.baselineValue})`
        : `${m.comparator} ${m.target}`;
    return `${m.lineStatus} · ${label} = ${last} vs ${need}`;
}
export function scorecardOverallStatus(metrics, deadlinePassed) {
    const graded = metrics.filter((m) => (m.kind ?? 'outcome') !== 'diagnostic');
    if (graded.length === 0)
        return 'active';
    if (graded.every((m) => ['achieved', 'healthy', 'pass'].includes(m.lineStatus))) {
        return 'completed';
    }
    if (deadlinePassed &&
        graded.some((m) => !['achieved', 'healthy', 'pass'].includes(m.lineStatus))) {
        return 'completed';
    }
    return 'active';
}
//# sourceMappingURL=trial-scorecard.js.map